import React from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { db } from 'firebase/firebase';
import { collection } from 'firebase/firestore';

import BoxContainer from 'components/BoxContainer';
import Title from 'components/BoxContainer/components/Title';
import FilterContainer from 'components/FilterContainer';

import ExternalCompanyForm from 'packages/externalCompanies/components/ExternalCompanyForm';
import ExternalCompanyItem from 'packages/externalCompanies/components/ExternalCompanyItem';
import { useFirestoreQuery, useToggle } from 'packages/utils';

import { COMPANIES } from 'util/firebase-operations/externalCompanies/constants';
import IntlMessages from 'util/IntlMessages';
import safeExecute from 'util/safeExecute';

import CompanyService from '../../../../services/CompanyService';

export default function ExternalCompanies() {
  const intl = useIntl();
  const companyIdRef = React.useRef(null);
  const orgId = useSelector(state => state.organizations.organization.id);

  const [externalCompanyFormVisible, setExternalCompanyFormVisible] = React.useState(false);
  const [loadingToggle, onLoadingToggle] = useToggle();

  const externalCompaniesRef = collection(db, 'organizations', orgId, COMPANIES);
  const { data = [], loading } = useFirestoreQuery(externalCompaniesRef, [orgId]);

  const onCloseExternalCompanyForm = () => {
    companyIdRef.current = null;
    setExternalCompanyFormVisible(false);
  };

  const onDeleteExternalCompany = id => {
    const company = new CompanyService();
    safeExecute(async () => {
      Modal.confirm({
        title: intl.formatMessage({ id: 'general.confirmMessage' }),
        icon: <ExclamationCircleOutlined />,
        okText: intl.formatMessage({ id: 'general.yes' }),
        cancelText: intl.formatMessage({ id: 'general.no' }),
        onOk: async () => {
          onLoadingToggle();
          await company.deleteCompany({ organizationId: orgId, companyId: id });
          onLoadingToggle();
        },
      });
    });
  };

  return (
    <BoxContainer>
      <BoxContainer content shadow fixed>
        <FilterContainer
          title={<Title.Header value={<IntlMessages id="externalCompanies.title" />} />}
          buttonItems={[
            {
              iconName: 'add',
              action: () => setExternalCompanyFormVisible(true),
            },
          ]}
        />
      </BoxContainer>
      <BoxContainer content loading={loading || loadingToggle}>
        {data?.map(item => (
          <ExternalCompanyItem
            key={item.id}
            data={item}
            onEdit={() => {
              setExternalCompanyFormVisible(true);
              companyIdRef.current = item.id;
            }}
            onDelete={onDeleteExternalCompany}
          />
        ))}
      </BoxContainer>

      {externalCompanyFormVisible && (
        <ExternalCompanyForm
          onClose={onCloseExternalCompanyForm}
          visible={externalCompanyFormVisible}
          id={companyIdRef.current}
        />
      )}
    </BoxContainer>
  );
}
